import React, { useState, useEffect } from 'react';
import { SchedulerData, ViewType, DATE_FORMAT } from "react-big-schedule";
import dayjs from "dayjs";
import 'dayjs/locale/es'; // Import Spanish locale for dayjs
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from 'react-redux';
import { getScheduleWorkOrders, updateWorkOrderDuration, updateWorkOrderStart, updateWorkOrderChangeDriver } from '../redux/slice';
import { openModalCreateEditWorkOrder } from '../../../.globals/redux/actions';
import { NotificationManager } from 'Components/Notifications';

/**
 * Custom hook for managing scheduler functionality
 * Handles all event-related operations, view management, and navigation
 * @param {Array} initialEvents - Initial array of events to populate the scheduler
 * @returns {Object} Scheduler methods and state
 */
const useScheduler = (initialEvents = [], selectedDrivers = [], formData = {}, loadPreference = false, hasFilters) => {

  const [dateRange, setDateRange] = useState({
    start: dayjs(),
    end: dayjs()
  })
  const [schedulerData, setSchedulerData] = useState(null);
  const [events, setEvents] = useState(initialEvents);
  const [originalEvents, setOriginalEvents] = useState([]);
  const [modifiedEvents, setModifiedEvents] = useState({});
  const [page, setPage] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);
  const [tempEventData, setTempEventData] = useState(null);
  const [resourceFilter, setResourceFilter] = useState('');
  const [filterData, setFilterData] = useState(formData);
  const [parametersFilter, setParametersFilter] = useState('')
  let loadDataWithFilters = false;

  // Estado para el filtro de status de eventos
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredEvents, setFilteredEvents] = useState(initialEvents);
  const [eventData, setEventData] = useState({
    name: '',
    description: ''
  });

  const messages = useIntl();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const { user } = useSelector(state => state.securityRedux);
  const { dateformat } = user;
  const scheduleWorkOrders = useSelector(state => state.workOrdersScheduleRedux.scheduleWorkOrders);
  const scheduleWorkOrdersLoading = useSelector(state => state.workOrdersScheduleRedux.scheduleWorkOrdersLoading);

  // Define SchedulerData outside useEffect to use it in other parts of the component
  const newSchedulerData = new SchedulerData(
    dayjs().format(DATE_FORMAT),
    ViewType.Month,
  );

  const hasNullValues = (data) => {
    const fieldsToCheck = ['severity', 'statusId', 'typeId', 'tagIds'];
    return !fieldsToCheck.some(field => data[field] !== null);
  };

  const applyChanges = () => {
    // Build new filterData
    const newFilterData = {
      severity: formData?.severity?.id || null,
      statusId: formData?.status?.id || null,
      typeId: formData?.type?.id || null,
      tagIds: formData?.tags?.length ? formData.tags.map(tag => tag.id) : null,
      dateRange: {
        start: formData?.rangeDate?.start || null,
        end: formData?.rangeDate?.end || null
      },
      customerIds: formData?.customers?.length ? formData.customers.map(customer => parseInt(customer.id)) : null
    };

    // Update state and dispatch immediately with new data
    setFilterData(newFilterData);
    setEvents([]);
    const rangeDataFilter = hasFilters() ? newFilterData.dateRange : dateRange;
    schedulerData.setEvents([]);
    const date = dayjs(rangeDataFilter.start).format('YYYY-MM-DD');
    schedulerData.setDate(date);
    // Dispatch with updated data
    dispatch(getScheduleWorkOrders({
      dateRange: rangeDataFilter,
      ...newFilterData,
      conditions: [],
      allDrivers: hasNullValues(newFilterData) ? true : false,
      limit: 50,
      offset: 0
    })).then(() => {
      loadDataWithFilters = false;
    });
  }

  useEffect(() => {
    if (loadPreference) {
      loadDataWithFilters = true;
      setTimeout(() => {
        applyChanges();
      }, 900);
    }
  }, [loadPreference])

  useEffect(() => {
    if (schedulerData) {
      const currentEvents = schedulerData.events;
      const idEvent = Object.keys(modifiedEvents)[0];
      const eventToUpdate = currentEvents.find(event => parseInt(event.id) === parseInt(idEvent));

      if (eventToUpdate) {
        const newEvent = {
          ...eventToUpdate,
          ...modifiedEvents[Object.keys(modifiedEvents)[0]]
        }
        schedulerData.removeEvent(eventToUpdate);
        schedulerData.addEvent(newEvent);

        const newEvents = schedulerData.events;

        setEvents(newEvents);
        setOriginalEvents(newEvents);
        updateViewModel(schedulerData);
      }
    }
  }, [modifiedEvents])

  /**
   * Updates a modified event in the local state
   * @param {string|number} eventId - The ID of the event to update
   * @param {Object} updates - The updates to apply to the event
   */
  const updateModifiedEvent = (eventId, updates) => {
    setModifiedEvents(prev => ({
      ...prev,
      [eventId]: {
        ...(prev[eventId] || {}),
        ...updates,
        isModified: true
      }
    }));
  };

  /**
   * Gets the most up-to-date version of events by merging server events with local modifications
   * @param {Array} serverEvents - Events received from the server
   * @returns {Array} Merged events with local modifications
   */
  const getMergedEvents = (serverEvents) => {
    if (!serverEvents) return [];

    return serverEvents.map(event => {
      const modifiedEvent = modifiedEvents[event.id];
      if (modifiedEvent && modifiedEvent.isModified) {
        return {
          ...event,
          ...modifiedEvent
        };
      }
      return event;
    });
  };

  useEffect(() => {
    if (!scheduleWorkOrdersLoading && scheduleWorkOrders && scheduleWorkOrders.items && scheduleWorkOrders.items.length > 0 && schedulerData) {

      // Get resources
      let resources = scheduleWorkOrders.items.map(item => ({
        id: `r${item.id}`,
        name: item.name,
        tagIds: item?.tagIds?.split(',') || []
      }));

      // If there are selected drivers, filter resources
      if (selectedDrivers && selectedDrivers.length > 0) {
        // Create a set of resource IDs to show (with "r" prefix)
        const selectedResourceIds = new Set(selectedDrivers.map(driver => `r${driver.id}`));

        // Filter resources by selected IDs
        resources = resources.filter(resource => selectedResourceIds.has(resource.id));
        const cloneResources = [...resources];
        let driverNotFound = selectedDrivers.filter(driver =>
          !cloneResources.some(selectedDriver => selectedDriver.id === `r${driver.id}`)
        );

        if (driverNotFound.length > 0) {
          const processedDrivers = new Set();

          driverNotFound.forEach(driver => {
            if (!processedDrivers.has(driver.id)) {
              processedDrivers.add(driver.id);
              resources.push({
                id: `r${driver.id}`,
                name: driver.name,
              });
            }
          });

          const missingDriverIds = Array.from(processedDrivers);
          if (missingDriverIds.length > 0 && !selectedDriversIds.some(id => missingDriverIds.includes(id))) {
            dispatch(getScheduleWorkOrders({
              ...filterData,
              driverIds: missingDriverIds,
              dateRange: dateRange,
              limit: 50,
              offset: 0
            })).unwrap().then(response => {
              if (response?.items?.length > 0) {
                const newResources = response.items.map(item => ({
                  id: `r${item.id}`,
                  name: item.name,
                  tagIds: item?.tagIds?.split(',') || []
                }));
                resources = resources.concat(newResources);
              }
            });
          }
        }
      }

      const currentEvents = schedulerData.events || [];
      const currentEventsMap = new Map();
      currentEvents.forEach(event => {
        currentEventsMap.set(event.id, event);
      });

      const processedEventIds = new Set();
      let newEvents = [];

      scheduleWorkOrders.items.forEach(item => {
        if (selectedDrivers && selectedDrivers.length > 0) {
          const isSelected = selectedDrivers.some(driver => `r${driver.id}` === `r${item.id}`);
          if (!isSelected) return;
        }
        if (!item.workOrders || !Array.isArray(item.workOrders)) return;

        const driverEvents = item.workOrders.map(workOrder => {
          const {
            id,
            scheduledDate,
            customerName,
            contactName,
            contactEmail,
            contactPhone,
            statusName,
            durationInMinutes,
            workOrderNumber,
            severity,
            description,
            workOrderTypeId,
            statusColor,
            estimatedDurationMins,
            workOrderAddress,
            workOrderTypeName
          } = workOrder || {};

          if (processedEventIds.has(id)) return null;
          processedEventIds.add(id);
          if (!scheduledDate) return null;
          if (currentEventsMap.has(id)) {
            const existingEvent = currentEventsMap.get(id);
            const updatedEvent = {
              ...existingEvent,
              title: workOrderNumber || existingEvent.title,
              status: statusName || existingEvent.status,
              bgColor: statusColor ? `#${statusColor}` : existingEvent.bgColor,
              driverName: item.name,
              customerName: customerName || existingEvent.customerName,
              contactName: contactName || existingEvent.contactName,
              contactEmail: contactEmail || existingEvent.contactEmail,
              contactPhone: contactPhone || existingEvent.contactPhone,
              description: description || existingEvent.description,
              severity: severity || existingEvent.severity,
              workOrderTypeName: workOrderTypeName || existingEvent.workOrderTypeName
            };

            return updatedEvent;
          }

          const finalDurationMins = estimatedDurationMins || durationInMinutes || 60;
          const previousEvent = currentEvents.find(e => e.id === id);

          const startTime = (previousEvent && previousEvent.updated_start)
            ? previousEvent.start
            : dayjs(scheduledDate).format('YYYY-MM-DD HH:mm:ss');

          const endTime = (previousEvent && previousEvent.updated_start)
            ? dayjs(previousEvent.start).add(finalDurationMins, 'minutes').format('YYYY-MM-DD HH:mm:ss')
            : dayjs(scheduledDate).add(finalDurationMins, 'minutes').format('YYYY-MM-DD HH:mm:ss');

          return {
            id: id,
            resourceId: `r${item.id}`,
            start: startTime,
            end: endTime,
            updated_start: previousEvent ? previousEvent.updated_start : false,
            scheduledDate: previousEvent && previousEvent.updated_start ? previousEvent.scheduledDate : scheduledDate,
            title: workOrderNumber || "No number",
            bgColor: statusColor ? `#${statusColor}` : "#04ADEF",
            statusColor,
            movable: true,
            resizable: true,
            showPopover: true,
            startResizable: true,
            type: workOrderTypeName,
            driverName: item.name,
            status: statusName,
            customerName,
            contactName,
            contactEmail,
            workOrderAddress,
            contactPhone,
            severity,
            description,
            workOrderTypeId,
            durationInMinutes: finalDurationMins,
            estimatedDurationMins: finalDurationMins
          };
        }).filter(Boolean);

        newEvents = newEvents.concat(driverEvents);
      });

      currentEvents.forEach(event => {
        if (!processedEventIds.has(event.id)) {
          newEvents.push(event);
          processedEventIds.add(event.id);
        }
      });

      const eventsWithUpdatesApplied = getMergedEvents(newEvents);

      schedulerData.setResources(resources);
      schedulerData.setEvents(eventsWithUpdatesApplied);
      setEvents(eventsWithUpdatesApplied);
      setOriginalEvents(eventsWithUpdatesApplied);
      updateViewModel(schedulerData);

      if (parametersFilter) {
        setTimeout(() => {
          handleFilterChange(parametersFilter, eventsWithUpdatesApplied);
        }, 100);
      }
    }
  }, [scheduleWorkOrdersLoading, selectedDrivers, filterData]);

  useEffect(() => {
    newSchedulerData.setSchedulerLocale(messages.locale);
    newSchedulerData.setCalendarPopoverLocale(messages.locale);

    Object.assign(newSchedulerData.config, {
      creatable: true,
      movable: true,
      resizable: true,
      schedulerContentHeight: '64vh',
      checkConflict: true,
      showAgenda: false,
      defaultEventBgColor: '#04ADEF',
      selectedAreaColor: '#04ADEF',
      nonWorkingTimeHeadBgColor: '#fff',
      nonWorkingTimeBodyBgColor: '#fff',
      minuteStep: 10,
      responsiveByParent: true,
      schedulerMaxHeight: 0,
      schedulerWidth: '100%',
      besidesWidth: 45,
      eventItemPopoverTrigger: 'click',
      resourceName: formatMessage({ id: 'schedule_workOrder_drivers' }),
      timeCellWidth: 2,
      views: [
        { viewName: formatMessage({ id: 'schedule_workOrder_view_day' }), viewType: ViewType.Day, showAgenda: false, isEventPerspective: false },
        { viewName: formatMessage({ id: 'schedule_workOrder_view_week' }), viewType: ViewType.Week, showAgenda: false, isEventPerspective: false },
        { viewName: formatMessage({ id: 'schedule_workOrder_view_month' }), viewType: ViewType.Month, showAgenda: false, isEventPerspective: false },
      ]
    });

    setSchedulerData(newSchedulerData);
    setRefreshCount(prev => prev + 1);

    const startDate = dayjs(newSchedulerData.startDate)
    const endDate = dayjs(newSchedulerData.endDate)

    setDateRange({ start: startDate, end: endDate });
    setPage(0);

    if (selectedDrivers && Array.isArray(selectedDrivers)) {
      setPreviousSelectedDrivers([...selectedDrivers]);
      setSelectedDriversIds(selectedDrivers.map(driver => driver.id));
    }
  }, []);

  useEffect(() => {
    if (schedulerData) {
      const currentEvents = schedulerData.events;

      schedulerData.setSchedulerLocale(messages.locale);
      schedulerData.setCalendarPopoverLocale(messages.locale);
      schedulerData.config.resourceName = formatMessage({ id: 'schedule_workOrder_drivers' });
      schedulerData.config.views = [
        { viewName: formatMessage({ id: 'schedule_workOrder_view_day' }), viewType: ViewType.Day, showAgenda: false, isEventPerspective: false },
        { viewName: formatMessage({ id: 'schedule_workOrder_view_week' }), viewType: ViewType.Week, showAgenda: false, isEventPerspective: false },
        { viewName: formatMessage({ id: 'schedule_workOrder_view_month' }), viewType: ViewType.Month, showAgenda: false, isEventPerspective: false }
      ];
      dayjs.locale(messages.locale);

      schedulerData.setEvents(currentEvents);

      refreshScheduler();
    }
  }, [messages.locale]);

  useEffect(() => {
    if (schedulerData) {
      if (loadDataWithFilters) return;
      handlePagination(0, 50, dateRange);
    }
  }, [dateRange]);

  useEffect(() => {
    if (schedulerData) {
      const filtered = statusFilter === 'all'
        ? events
        : events.filter(event => event.status === statusFilter);

      setFilteredEvents(filtered);

      updateViewModel(schedulerData);
    }
  }, [statusFilter, events]);

  /**
   * Updates the view model and triggers a refresh
   * @param {Object} newViewModel - The new scheduler view model
   */
  const updateViewModel = (newViewModel) => {
    setSchedulerData(newViewModel);
    setRefreshCount(prev => prev + 1);
  };

  /**
   * Handles pagination for loading more events
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {Object} dateRange - Date range for the query
   */
  const handlePagination = (page, limit, dateRange) => {
    if (schedulerData) {
      const currentEvents = schedulerData.events || [];
      setEvents(currentEvents);
    }

    dispatch(getScheduleWorkOrders({
      ...filterData,
      conditions: [],
      allDrivers: hasNullValues(filterData) ? true : false,
      dateRange: dateRange,
      limit: limit,
      offset: page * limit
    }));
  };

  /**
   * Handles resource filtering
   * @param {string} filterValue - The filter value to apply
   * @param {Array} eventsToFilter - Optional array of events to filter (defaults to originalEvents)
   */
  const handleFilterChange = (filterValue, eventsToFilter = null) => {
    setParametersFilter(filterValue);

    const eventsSource = eventsToFilter || originalEvents;

    if (!filterValue || filterValue.trim() === '') {
      schedulerData.setEvents(eventsSource);
      updateViewModel(schedulerData);
      setResourceFilter('');
      return;
    }

    const searchTerm = filterValue.toLowerCase().trim();

    const resultFilterEvents = eventsSource.filter(event => {
      // Search in event description
      const description = (event.description || '').toLowerCase();
      const descriptionMatch = description.includes(searchTerm);

      // Search in customer name
      const customerName = (event.customerName || '').toLowerCase();
      const customerMatch = customerName.includes(searchTerm);

      // Search in work order type
      const type = (event.type || '').toLowerCase();
      const typeMatch = type.includes(searchTerm);

      // Search in status
      const status = (event.status || '').toLowerCase();
      const statusMatch = status.includes(searchTerm);

      // Search in driver name
      const driverName = (event.driverName || '').toLowerCase();
      const driverMatch = driverName.includes(searchTerm);

      // Search in work order number
      const title = (event.title || '').toLowerCase();
      const titleMatch = title.includes(searchTerm);

      // Search in contact name
      const contactName = (event.contactName || '').toLowerCase();
      const contactMatch = contactName.includes(searchTerm);

      // Search in address if it exists
      let addressMatch = false;
      if (event.workOrderAddress) {
        try {
          const address = typeof event.workOrderAddress === 'string'
            ? JSON.parse(event.workOrderAddress)
            : event.workOrderAddress;
          const addressText = (address?.formatted || '').toLowerCase();
          addressMatch = addressText.includes(searchTerm);
        } catch (e) {
          // If there is an error parsing, search in the string directly
          const addressText = (event.workOrderAddress || '').toLowerCase();
          addressMatch = addressText.includes(searchTerm);
        }
      }

      // Return true if any of the fields match
      return descriptionMatch ||
        customerMatch ||
        typeMatch ||
        statusMatch ||
        driverMatch ||
        titleMatch ||
        contactMatch ||
        addressMatch;
    });
    schedulerData.setEvents(resultFilterEvents);

    updateViewModel(schedulerData);
    setResourceFilter(filterValue);
  };

  /**
   * Handles status filtering
   * @param {string} newStatus - The new status to filter by
   */
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
  };

  /**
   * Handles the editing of work order duration and start time
   * Implements optimistic updates and error handling
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} event - The event being modified
   * @param {string} newValue - The new time value
   * @param {string} type - The type of edit ('start' or 'end')
   */
  const handleEditDurationWorkOrder = (schedulerData, event, newValue, type) => {
    if (type === 'end') {
      try {
        const id = event.id;
        const estimatedDurationMins = dayjs(newValue).diff(dayjs(event.start), 'minutes');

        // Actualizar el evento en el estado local
        updateModifiedEvent(id, {
          end: newValue,
          estimatedDurationMins
        });

        // Optimistic update
        const updatedEvent = {
          ...event,
          end: newValue,
          estimatedDurationMins
        };

        schedulerData.removeEvent(event);
        schedulerData.addEvent(updatedEvent);
        updateViewModel(schedulerData);

        dispatch(updateWorkOrderDuration({
          id,
          estimatedDurationMins
        })).catch((error) => {
          console.error("Error updating work order duration", error);
          // Restore event after date change
          setModifiedEvents(prev => {
            const { [id]: _, ...rest } = prev;
            return rest;
          });
          schedulerData.removeEvent(updatedEvent);
          schedulerData.addEvent(event);
          updateViewModel(schedulerData);
        });
      } catch (error) {
        console.error("Error updating work order duration", error);
      }
    } else if (type === 'start') {
      try {
        const id = event.id;
        const scheduledDate = dayjs(newValue).format('YYYY-MM-DD HH:mm:ss');
        const estimatedDurationMins = dayjs(event.end).diff(dayjs(newValue), 'minutes');

        updateModifiedEvent(id, {
          start: newValue,
          scheduledDate,
          estimatedDurationMins
        });

        const updatedEvent = {
          ...event,
          start: newValue,
          scheduledDate,
          estimatedDurationMins
        };

        schedulerData.removeEvent(event);
        schedulerData.addEvent(updatedEvent);
        updateViewModel(schedulerData);

        dispatch(updateWorkOrderStart({
          id,
          scheduledDate,
          estimatedDurationMins
        })).catch((error) => {
          console.error("Error updating work order start", error);
          setModifiedEvents(prev => {
            const { [id]: _, ...rest } = prev;
            return rest;
          });
          schedulerData.removeEvent(updatedEvent);
          schedulerData.addEvent(event);
          updateViewModel(schedulerData);
        });
      } catch (error) {
        console.error("Error updating work order start", error);
      }
    }
  };

  /**
   * Navigates to the previous time frame
   * Preserves event modifications when changing view
   * @param {Object} schedulerData - The scheduler instance data
   */
  const prevClick = (schedulerData) => {
    const currentEvents = schedulerData.events;
    schedulerData.prev();
    schedulerData.setEvents(currentEvents);
    updateViewModel(schedulerData);
    const startDate = dayjs(schedulerData.startDate);
    let endDate;

    if (schedulerData.viewType === ViewType.Day) {
      endDate = startDate.endOf('day');
    } else {
      endDate = dayjs(schedulerData.endDate);
    }

    setDateRange({
      start: startDate,
      end: endDate
    });

    setPage(0);
    setParametersFilter('');
  };

  /**
   * Navigates to the next time frame
   * Preserves event modifications when changing view
   * @param {Object} schedulerData - The scheduler instance data
   */
  const nextClick = (schedulerData) => {
    const currentEvents = schedulerData.events;

    schedulerData.next();

    schedulerData.setEvents(currentEvents);

    updateViewModel(schedulerData);

    const startDate = dayjs(schedulerData.startDate);
    let endDate;

    if (schedulerData.viewType === ViewType.Day) {
      endDate = startDate.endOf('day');
    } else {
      endDate = dayjs(schedulerData.endDate);
    }

    setDateRange({
      start: startDate,
      end: endDate
    });

    setPage(0);
    setParametersFilter('');
  };

  /**
   * Handles date selection from the date picker
   * Updates view while maintaining event modifications
   * @param {Object} schedulerData - The scheduler instance data
   * @param {string} date - The selected date
   */
  const onSelectDate = (schedulerData, date) => {
    const currentEvents = schedulerData.events;
    schedulerData.setDate(date);
    schedulerData.setEvents(currentEvents);
    const startDate = dayjs(schedulerData.startDate);
    let endDate;
    if (schedulerData.viewType === ViewType.Day) {
      endDate = startDate.endOf('day');
    } else {
      endDate = dayjs(schedulerData.endDate);
    }

    setDateRange({
      start: startDate,
      end: endDate
    });

    setPage(0);
    setParametersFilter('');
  };

  /**
   * Handles view type changes (day, week, month)
   * Preserves event modifications when changing view type
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} view - The new view configuration
   */
  const onViewChange = (schedulerData, view) => {
    const currentEvents = schedulerData.events;
    schedulerData.setViewType(
      view.viewType,
      view.showAgenda,
      view.isEventPerspective
    );

    schedulerData.setEvents(currentEvents);
    updateViewModel(schedulerData);

    const startDate = dayjs(schedulerData.startDate);
    let endDate;

    if (view.viewType === ViewType.Day) {
      endDate = startDate.endOf('day');
    } else {
      endDate = dayjs(schedulerData.endDate);
    }

    setDateRange({
      start: startDate,
      end: endDate
    });

    setPage(0);
    setParametersFilter('');
  };

  /**
   * Handles clicks on event items
   * Opens modal with event details
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} event - The clicked event
   */
  const eventItemClick = (schedulerData, event) => {

  };

  /**
   * Creates a new event when a time slot is selected
   * @param {Object} schedulerData - The scheduler instance data
   * @param {string} slotId - The resource ID
   * @param {string} slotName - The resource name
   * @param {string} start - Start time
   * @param {string} end - End time
   * @param {string} type - Event type
   * @param {Object} item - Additional item data
   */
  const newEvent = (schedulerData, slotId, slotName, start, end, type, item) => {
    setTempEventData({
      schedulerData,
      slotId,
      start,
      end
    });

    setEventData({
      name: '',
      description: '',
      status: 'pending'
    });

    const driverId = parseInt(slotId.replace('r', ''));
    const estimatedDurationMins = parseInt(dayjs(end).diff(dayjs(start), 'minutes'));
    const scheduledDate = dayjs(start).format('YYYY-MM-DD HH:mm:ss');
    const driver = schedulerData.resources.find(resource => resource.id === slotId);
    const preloadedData = {
      scheduledAt: scheduledDate,
      driver: {
        id: driverId,
        name: driver ? driver.name : slotName,
        tagIds: driver && driver.tagIds ? driver.tagIds : []
      },
      estimatedDurationMins: estimatedDurationMins
    };

    dispatch(openModalCreateEditWorkOrder({
      preloadedData
    }));
  };

  /**
   * Handles moving an event to a different time or resource
   * Implements optimistic updates and error handling
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} event - The event being moved
   * @param {string} slotId - The target resource ID
   * @param {string} slotName - The name of the target resource
   * @param {string} start - New start time
   * @param {string} end - New end time
   */
  const moveEvent = (schedulerData, event, slotId, slotName, start, end) => {
    const id = event.id;

    const oldEvent = { ...event };
    const startDate = dayjs(start).format('YYYY-MM-DD HH:mm:ss');
    const duration = dayjs(end).diff(dayjs(start), 'minutes');
    const driverId = parseInt(slotId.split('r')[1]);

    schedulerData.moveEvent(event, slotId, slotName, start, end);
    updateViewModel(schedulerData);

    dispatch(updateWorkOrderChangeDriver({
      id,
      driverId,
      scheduledDate: startDate,
      estimatedDurationMins: duration
    })).unwrap().then(response => {
      updateModifiedEvent(id, {
        start,
        end,
        driverName: slotName,
        resourceId: slotId,
        scheduledDate: startDate,
        estimatedDurationMins: duration,
        driverId,
        movedBetweenResources: true
      });
    }).catch(error => {
      NotificationManager.warning(
        formatMessage({ id: 'schedule_workOrder_move_message_error' }),
        formatMessage({ id: 'schedule_workOrder_move_title_error' }),
        5000,
        null,
        null,
        'Success',
      );
      schedulerData.removeEvent(event);
      schedulerData.moveEvent(oldEvent, oldEvent.resourceId, oldEvent.slotName, oldEvent.start, oldEvent.end);
      updateViewModel(schedulerData);
    });
  };
  /**
   * Handles event resizing (duration changes)
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} event - The event being resized
   * @param {string} start - New start time
   * @param {string} end - New end time
   */
  const resizeEvent = (schedulerData, event, start, end) => {
    try {
      if (event.startResizable === false) {
        schedulerData.updateEventEnd(event, end);
      } else {
        const updatedEvent = {
          ...event,
          start: start,
          end: end
        };

        schedulerData.removeEvent(event);
        schedulerData.addEvent(updatedEvent);
      }

      updateViewModel(schedulerData);
    } catch (error) {
      console.error("Error al redimensionar el evento:", error);
      if (schedulerData && event) {
        const originalEvent = events.find(e => e.id === event.id);
        if (originalEvent) {
          schedulerData.removeEvent(event);
          schedulerData.addEvent(originalEvent);
          updateViewModel(schedulerData);
        }
      }
    }
  };

  /**
   * Handles conflict detection between events
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} event - The event causing the conflict
   */
  const conflictOccurred = (schedulerData, event) => {
    NotificationManager.warning(
      formatMessage({ id: 'schedule_workOrder_conflict' }),
      formatMessage({ id: 'schedule_workOrder_title_conflict' }),
      5000,
      null,
      null,
      'Success',
    );
  };

  /**
   * Callback for when right scroll limit is reached during drag operations
   */
  const onScrollRight = (schedulerData, schedulerContent, maxScrollLeft) => {
    // Placeholder for future implementation
  };

  /**
   * Callback for when left scroll limit is reached during drag operations
   */
  const onScrollLeft = (schedulerData, schedulerContent, maxScrollLeft) => {
    // Placeholder for future implementation
  };

  /**
   * Callback for when top scroll limit is reached during drag operations
   */
  const onScrollTop = (schedulerData, schedulerContent, maxScrollTop) => {
    // Placeholder for future implementation
  };

  /**
   * Callback for when bottom scroll limit is reached during drag operations
   */
  const onScrollBottom = (schedulerData, schedulerContent, maxScrollTop) => {
    if (scheduleWorkOrders.total <= scheduleWorkOrders.items.length) return;
    const { scrollTop, scrollHeight, clientHeight } = schedulerContent;
    if (scrollHeight - scrollTop - clientHeight < 100 && !scheduleWorkOrdersLoading) {
      setPage(prevPage => {
        const nextPage = prevPage + 1;
        handlePagination(nextPage, 50, dateRange);
        return nextPage;
      });
    }
  };

  /**
   * Toggle expansion/collapse of resource groups
   */
  const toggleExpandFunc = (schedulerData, slotId) => {
    schedulerData.toggleExpandStatus(slotId);
    updateViewModel(schedulerData);
  };

  /**
   * Updates event start time
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} event - The event to update
   * @param {string} newStart - New start time
   */
  const updateEventStart = (schedulerData, event, newStart) => {
    try {
      handleEditDurationWorkOrder(schedulerData, event, newStart, 'start');
    } catch (error) {
      console.error("Error updating event start time:", error);
    }
  };

  /**
   * Updates event end time
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} event - The event to update
   * @param {string} newEnd - New end time
   */
  const updateEventEnd = (schedulerData, event, newEnd) => {
    try {
      handleEditDurationWorkOrder(schedulerData, event, newEnd, 'end');
    } catch (error) {
      console.error("Error updating event end time:", error);
    }
  };

  /**
   * Refreshes the scheduler view
   * Triggers a re-render with current state and reloads data from server
   */
  const refreshScheduler = () => {
    setResourceFilter('');
    setStatusFilter('all');
    dispatch(getScheduleWorkOrders({
      ...filterData,
      conditions: [],
      allDrivers: hasNullValues(filterData) ? true : false,
      dateRange: dateRange,
      limit: 50,
      offset: 0
    }));
    setRefreshCount(prev => prev + 1);
  };

  /**
   * Generates the popover template for event items
   * @param {Object} schedulerData - The scheduler instance data
   * @param {Object} eventItem - The event item
   * @param {string} title - Event title
   * @param {string} statusColor - Status color
   * @returns {JSX.Element} Popover template
   */
  const eventItemPopoverTemplateResolver = (schedulerData, eventItem, title, statusColor) => {
    const {
      id,
      resourceId,
      bgColor,
      status,
      customerName,
      contactName,
      type,
      contactEmail,
      contactPhone,
      severity,
      description,
      workOrderTypeId,
      durationInMinutes,
      driverName,
      start,
      end
    } = eventItem || {};

    let workOrderAddress = eventItem?.workOrderAddress;
    workOrderAddress = workOrderAddress ? JSON.parse(workOrderAddress) : {};
    workOrderAddress = workOrderAddress?.formatted || '';

    let severityList = [
      { id: "Low", name: messages['workOrder_createEdit_low'], color: "#50d6fa" },
      { id: "Medium", name: messages['workOrder_createEdit_medium'], color: "#222222" },
      { id: "High", name: messages['workOrder_createEdit_high'], color: "#ff685a" }
    ]

    let severityStyle = severityList.find((item) => item?.id === severity)
    let severityName = severityStyle?.name || severity
    let severityColor = severityStyle?.color || "#04ADEF"

    return (
      <div className="scheduler-popover">
        <div className="scheduler-popover__header">
          <div className="scheduler-popover__header-driver">
            <p className="scheduler-popover__header-driver-name">
              {driverName}
            </p>
            <p className="scheduler-popover__header-driver-severity" style={{ color: severityColor }}>
              {severityName}
            </p>
          </div>
          <p className="scheduler-popover__header-status" style={{ backgroundColor: bgColor }}>
            {status}
          </p>
        </div>

        <div className="scheduler-popover__time">
          <div className="scheduler-popover__time-point">
            <span className="scheduler-popover__time-point-hour">
              {dayjs(start).format('HH:mm')}
            </span>
            <p className="scheduler-popover__time-point-date">
              {dayjs(start).format('DD/MM/YYYY')}
            </p>
          </div>
          <div className="scheduler-popover__time-duration">
            <div className="scheduler-popover__time-duration-line"></div>
            <p className="scheduler-popover__time-duration-text">
              {durationInMinutes} min
            </p>
          </div>
          <div className="scheduler-popover__time-point">
            <span className="scheduler-popover__time-point-hour">
              {dayjs(end).format('HH:mm')}
            </span>
            <p className="scheduler-popover__time-point-date">
              {dayjs(end).format('DD/MM/YYYY')}
            </p>
          </div>
        </div>

        <div className="scheduler-popover__customer">
          <div className="scheduler-popover__customer-name">
            <span className="scheduler-popover__customer-name-text">
              {customerName}
            </span>
            <p className="scheduler-popover__customer-name-label">
              {formatMessage({ id: 'schedule_workOrder_customer' })}
            </p>
          </div>

          <div className="scheduler-popover__customer-contact">
            <div className="scheduler-popover__customer-contact-item">
              <span className="scheduler-popover__customer-contact-item-text">
                {contactPhone}
              </span>
              <p className="scheduler-popover__customer-contact-item-label">
                {formatMessage({ id: 'schedule_workOrder_phone' })}
              </p>
            </div>
            <div className="scheduler-popover__customer-contact-item">
              <span className="scheduler-popover__customer-contact-item-text">
                {contactEmail}
              </span>
              <p className="scheduler-popover__customer-contact-item-label">
                {formatMessage({ id: 'schedule_workOrder_email' })}
              </p>
            </div>
          </div>
        </div>

        <div className="scheduler-popover__details">
          <div className="scheduler-popover__details-address">
            <span className="scheduler-popover__details-address-text">
              {workOrderAddress}
            </span>
            <p className="scheduler-popover__details-address-label">
              {formatMessage({ id: 'schedule_workOrder_address' })}
            </p>
          </div>
          <div className="scheduler-popover__details-description">
            <span className="scheduler-popover__details-description-text">
              {description}
            </span>
            <p className="scheduler-popover__details-description-label">
              {formatMessage({ id: 'schedule_workOrder_description' })}
            </p>
          </div>
          <div className="scheduler-popover__details-info">
            <div className="scheduler-popover__details-info-item">
              <span className="scheduler-popover__details-info-item-text">
                {type}
              </span>
              <p className="scheduler-popover__details-info-item-label">
                {formatMessage({ id: 'schedule_workOrder_type' })}
              </p>
            </div>
            <div className="scheduler-popover__details-info-item">
              <span className="scheduler-popover__details-info-item-text">
                {title}
              </span>
              <p className="scheduler-popover__details-info-item-label">
                {formatMessage({ id: 'schedule_workOrder_workOrderNumber' })}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const [selectedDriversIds, setSelectedDriversIds] = useState([]);
  const [previousSelectedDrivers, setPreviousSelectedDrivers] = useState([]);

  useEffect(() => {
    if (selectedDrivers && Array.isArray(selectedDrivers)) {
      // Extract IDs from the current selection
      const currentSelectedIds = selectedDrivers.map(driver => driver.id);

      // Check if any driver was deselected by comparing with previous selection
      const wasDriverDeselected = previousSelectedDrivers.length > selectedDrivers.length;
      if (wasDriverDeselected) {
        setEvents([]);
        if (schedulerData) {
          schedulerData.setEvents([]);
          dispatch(getScheduleWorkOrders({
            dateRange: dateRange,
            ...filterData,
            conditions: [],
            allDrivers: hasNullValues(filterData) ? true : false,
            limit: 50,
            offset: 0
          }));
        }
      }

      setPreviousSelectedDrivers([...selectedDrivers]);
      setSelectedDriversIds(currentSelectedIds);
    }
  }, [selectedDrivers]);

  return {
    // Estados
    schedulerData,
    refreshCount,
    eventData,
    tempEventData,
    resourceFilter,
    statusFilter,
    filteredEvents,
    setEventData,
    handleFilterChange,
    handleStatusFilterChange,
    prevClick,
    nextClick,
    onSelectDate,
    onViewChange,
    eventItemClick,
    newEvent,
    moveEvent,
    resizeEvent,
    onScrollLeft,
    onScrollRight,
    onScrollTop,
    onScrollBottom,
    toggleExpandFunc,
    updateEventStart,
    updateEventEnd,
    applyChanges,
    conflictOccurred,
    eventItemPopoverTemplateResolver,
    refreshScheduler
  };
};

export default useScheduler; 